import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import * as Sentry from '@sentry/react';
import { useLocation } from 'react-router-dom';

import { useSetAnalyticsGlobal } from '@cast/analytics';
import {
  organizationQueryKey,
  AUTH_PROVIDER_QUERY_KEY,
  IS_PROD,
} from '@cast/constants';
import { Organization } from '@cast/types';
import {
  clearQueryParamSilently,
  getQueryParam,
  setQueryParamSilently,
  isE2E,
} from '@cast/utils';

import {
  useCurrentOrganizationId,
  useOrganizations,
  useSetCurrentOrganizationId,
} from './organization.state';

import {
  buildOrganizationSwitchUrl,
  DEFAULT_ORGANIZATION_SWITCH_PATH,
  getOrganizationDefaultRoute,
} from './navigation';
import { useNavigate } from './useNavigate';
import { useEnterpriseIdFromRoute } from './useEnterpriseIdFromRoute';

import {
  findOrganization,
  OrganizationContext,
} from '@cast/shared-organization-context-data-access';

type UseOrganizationsStateArgs = {
  organizationSwitchPath: string;
};

type HardReloadToHomeArgs = {
  organizationId: Organization['id'];
  organizationSwitchPath: string;
};

const useOrganizationsState = ({
  organizationSwitchPath,
}: UseOrganizationsStateArgs) => {
  const organizations = useOrganizations();
  const currentOrganizationId = useCurrentOrganizationId();
  const setCurrentOrganizationId = useSetCurrentOrganizationId();

  const currentOrganization = useMemo(() => {
    return findOrganization(currentOrganizationId, organizations);
  }, [organizations, currentOrganizationId]);
  const enterprise = useMemo(() => {
    return findOrganization(currentOrganization?.parentId, organizations);
  }, [organizations, currentOrganization?.parentId]);

  useSetAnalyticsGlobal('organizationId', currentOrganization?.id);
  useSetAnalyticsGlobal('organizationName', currentOrganization?.name);
  useSetAnalyticsGlobal(
    'isInternalOrganization',
    currentOrganization?.internal
  );
  useSetAnalyticsGlobal('enterpriseId', enterprise?.id);
  useSetAnalyticsGlobal('enterpriseName', enterprise?.name);

  const location = useLocation();
  const enterpriseRouteId = useEnterpriseIdFromRoute();
  const enterpriseOrgId = enterpriseRouteId ?? enterprise?.id;

  const orgIdInQuery = getQueryParam(organizationQueryKey);

  const navigate = useNavigate();

  const handleSetOrganization = useCallback(
    (
      newOrgId: Organization['id'],
      { skipRedirect }: { skipRedirect?: boolean } = {}
    ) => {
      const newOrganization = findOrganization(newOrgId, organizations);

      if (!newOrganization) {
        if (skipRedirect) {
          // Newly created orgs may not appear in listOrganizations immediately.
          setCurrentOrganizationId(newOrgId);
        } else {
          console.error('Trying to set non existing organization');
        }
        return;
      }

      const hardReloadToHome = ({
        organizationId,
        organizationSwitchPath: switchPath,
      }: HardReloadToHomeArgs) => {
        // Hard reload clears React Query cache for a new org, and also lets
        // re-selecting the current org escape forbidden / deep pages.
        // Skip setCurrentOrganizationId — bootstrap reads ?org= from the URL.
        window.location.href = buildOrganizationSwitchUrl({
          organizationId,
          path: switchPath,
        });
      };

      if (currentOrganization && currentOrganizationId !== newOrgId) {
        if (skipRedirect) {
          setCurrentOrganizationId(newOrgId);
          return;
        }

        if (window.location.pathname.startsWith('/organization')) {
          setCurrentOrganizationId(newOrgId);
          // Navigate explicitly to avoid a race condition
          // where the searchParamsAtom write (triggered by setCurrentOrganizationId)
          // re-commits the current pathname to the router,
          // overwriting the redirect from useOrganizationRouteValidation's useEffect
          navigate(getOrganizationDefaultRoute(newOrganization), {
            replace: true,
          });
          return;
        }

        hardReloadToHome({
          organizationId: newOrgId,
          organizationSwitchPath,
        });
        return;
      }

      // Re-selecting the current org → same hard redirect to product home.
      if (
        currentOrganization &&
        currentOrganizationId === newOrgId &&
        !skipRedirect &&
        window.location.pathname !== organizationSwitchPath
      ) {
        hardReloadToHome({
          organizationId: newOrgId,
          organizationSwitchPath,
        });
        return;
      }

      setCurrentOrganizationId(newOrgId);
    },
    [
      organizations,
      currentOrganization,
      currentOrganizationId,
      setCurrentOrganizationId,
      navigate,
      organizationSwitchPath,
    ]
  );

  useEffect(() => {
    const isForbidden = location.pathname.includes('/forbidden');
    // Add current org id to query if not present
    if (
      currentOrganizationId &&
      currentOrganizationId !== orgIdInQuery &&
      !isForbidden
    ) {
      setQueryParamSilently(organizationQueryKey, currentOrganizationId);
    }
    clearQueryParamSilently(AUTH_PROVIDER_QUERY_KEY);
  }, [
    currentOrganizationId,
    setCurrentOrganizationId,
    orgIdInQuery,
    location,
    enterpriseOrgId,
    organizations,
  ]);

  return {
    organizations,
    isReady: !!currentOrganizationId,
    currentOrganization,
    setCurrentOrganization: handleSetOrganization,
    enterpriseOrgId,
  };
};

type OrganizationsProviderProps = PropsWithChildren<{
  /**
   * Hard-nav landing path when switching org outside `/organization` routes.
   * Classic: `/dashboard`. OneClick: `/clusters`.
   */
  organizationSwitchPath?: string;
}>;

export const OrganizationsProvider = ({
  children,
  organizationSwitchPath = DEFAULT_ORGANIZATION_SWITCH_PATH,
}: OrganizationsProviderProps) => {
  const state = useOrganizationsState({ organizationSwitchPath });
  const canViewSentry = !isE2E() && IS_PROD;

  useEffect(() => {
    if (canViewSentry && state.currentOrganization) {
      Sentry.getGlobalScope().setTags({
        organization: state.currentOrganization?.name,
        organizationId: state.currentOrganization?.id,
      });
    }
  }, [state.currentOrganization, canViewSentry]);

  return (
    <OrganizationContext.Provider value={state}>
      {children}
    </OrganizationContext.Provider>
  );
};
